



























































































































import { Vue, Component, Prop, Watch } from 'vue-property-decorator'
import { v4 as uuid } from 'uuid'
import {
	CAMPOS_QUE_DEPENDEM_DE_TABELA_DE_PRECO,
	CampoDeEtiqueta,
	CampoDeEtiquetaDisponivel,
	CampoDoModeloDeEtiqueta,
	TipoDeAlinhamentoDeTexto,
	montarTextoDePrecoDaTabela,
} from '@/models/ModeloDeEtiqueta'
import type { TamanhoDaEtiqueta } from '@/models/EtiquetaDePreco'

const EXEMPLO_NOME_DA_TABELA = 'CARTAO CREDITO PARCELADO'
const EXEMPLO_LIMITACAO = '- Limitação 999'

const ESCALA_MAXIMA_PX_POR_CM = 55
const LARGURA_MAXIMA_DO_CANVAS_PX = 800
const PONTOS_POR_CM = 28.35

const ICONE_POR_CAMPO: Record<CampoDeEtiqueta, string> = {
	NOME_DA_LOJA: 'mdi-storefront-outline',
	SKU_PRODUTO_PAI: 'mdi-tag-outline',
	SKU_PRODUTO: 'mdi-pound',
	NOME_DO_PRODUTO: 'mdi-package-variant',
	CODIGO_DE_BARRAS: 'mdi-barcode',
	TEXTO_DO_EAN: 'mdi-numeric',
	PRECO_DO_PRODUTO: 'mdi-currency-usd',
	PRECO_DA_TABELA: 'mdi-cash-multiple',
	TRACO: 'mdi-minus',
}

@Component
export default class EditorDeLayoutDeEtiqueta extends Vue {
	@Prop({ type: Array, required: true }) value!: CampoDoModeloDeEtiqueta[]
	@Prop({ type: Object, required: true }) tamanho!: TamanhoDaEtiqueta
	@Prop({ type: Array, required: true }) camposDisponiveis!: CampoDeEtiquetaDisponivel[]

	campos: CampoDoModeloDeEtiqueta[] = []
	campoSelecionadoIndex: number | null = null

	arrastando: { indice: number; offsetXPx: number; offsetYPx: number } | null = null
	redimensionando: { indice: number; xInicialPx: number; yInicialPx: number; larguraInicial: number; alturaInicial: number } | null = null

	created() {
		this.campos = this.value || []
	}

	get escala(): number {
		const largura = this.tamanho.larguraEtiqueta || 1
		return Math.min(ESCALA_MAXIMA_PX_POR_CM, LARGURA_MAXIMA_DO_CANVAS_PX / largura)
	}

	get estiloDoCanvas() {
		return {
			width: `${(this.tamanho.larguraEtiqueta || 0) * this.escala}px`,
			height: `${(this.tamanho.alturaEtiqueta || 0) * this.escala}px`,
		}
	}

	get campoSelecionado(): CampoDoModeloDeEtiqueta | null {
		if (this.campoSelecionadoIndex === null) return null
		return this.campos[this.campoSelecionadoIndex] || null
	}

	get campoDependeDeTabela(): boolean {
		const campo = this.campoSelecionado?.campo
		return !!campo && CAMPOS_QUE_DEPENDEM_DE_TABELA_DE_PRECO.includes(campo)
	}

	get avisosDeSobreposicao(): string[] {
		const MARGEM_MINIMA_CM = 0.1
		const avisos: string[] = []
		for (let i = 0; i < this.campos.length; i++) {
			for (let j = i + 1; j < this.campos.length; j++) {
				if (this.seSobrepoem(this.campos[i], this.campos[j], MARGEM_MINIMA_CM)) {
					avisos.push(`${this.rotuloDoCampo(this.campos[i])} e ${this.rotuloDoCampo(this.campos[j])}`)
				}
			}
		}
		return avisos
	}

	seSobrepoem(a: CampoDoModeloDeEtiqueta, b: CampoDoModeloDeEtiqueta, margemCm: number): boolean {
		const semGapNoEixoX = a.x + a.largura + margemCm > b.x && b.x + b.largura + margemCm > a.x
		const semGapNoEixoY = a.y + a.altura + margemCm > b.y && b.y + b.altura + margemCm > a.y
		return semGapNoEixoX && semGapNoEixoY
	}

	iconeDoCampo(chave: CampoDeEtiqueta): string {
		return ICONE_POR_CAMPO[chave] || 'mdi-format-text'
	}

	tipoDeDado(campo: CampoDoModeloDeEtiqueta): string {
		return this.camposDisponiveis.find(disponivel => disponivel.chave === campo.campo)?.tipoDeDado || 'TEXTO'
	}

	rotuloDoCampo(campo: CampoDoModeloDeEtiqueta): string {
		return this.camposDisponiveis.find(disponivel => disponivel.chave === campo.campo)?.rotulo || ''
	}

	valorDeExemplo(campo: CampoDoModeloDeEtiqueta): string {
		if (campo.campo === 'PRECO_DA_TABELA') {
			return montarTextoDePrecoDaTabela(
				campo,
				EXEMPLO_NOME_DA_TABELA,
				this.valorDeExemploDoCatalogo('PRECO_DA_TABELA'),
				EXEMPLO_LIMITACAO,
			)
		}
		return this.valorDeExemploDoCatalogo(campo.campo)
	}

	valorDeExemploDoCatalogo(chave: CampoDeEtiqueta | null): string {
		return this.camposDisponiveis.find(disponivel => disponivel.chave === chave)?.valorDeExemplo || ''
	}

	estiloDoCampo(campo: CampoDoModeloDeEtiqueta, indice: number) {
		const alinhamento: Record<TipoDeAlinhamentoDeTexto, string> = {
			ESQUERDA: 'left',
			CENTRO: 'center',
			DIREITA: 'right',
		}
		return {
			left: `${campo.x * this.escala}px`,
			top: `${campo.y * this.escala}px`,
			width: `${campo.largura * this.escala}px`,
			height: `${campo.altura * this.escala}px`,
			fontSize: `${this.tamanhoDaFonteEmPx(campo)}px`,
			fontWeight: campo.negrito ? 'bold' : 'normal',
			textAlign: alinhamento[campo.alinhamento] || 'left',
			justifyContent: alinhamento[campo.alinhamento] === 'center' ? 'center' : alinhamento[campo.alinhamento] === 'right' ? 'flex-end' : 'flex-start',
			zIndex: indice === this.campoSelecionadoIndex ? 2 : 1,
		}
	}

	tamanhoDaFonteEmPx(campo: CampoDoModeloDeEtiqueta): number {
		return Math.max(6, (campo.tamanhoDaFonte / PONTOS_POR_CM) * this.escala)
	}

	adicionarCampo(disponivel: CampoDeEtiquetaDisponivel, xCm?: number, yCm?: number) {
		const ehCodigoDeBarras = disponivel.chave === 'CODIGO_DE_BARRAS'
		const larguraEtiqueta = this.tamanho.larguraEtiqueta || 0
		const alturaEtiqueta = this.tamanho.alturaEtiqueta || 0
		const largura = ehCodigoDeBarras ? Math.min(larguraEtiqueta * 0.85, larguraEtiqueta - 0.2) : Math.min(3, larguraEtiqueta || 3)
		const altura = ehCodigoDeBarras ? Math.max(1.5, alturaEtiqueta * 0.3) : 1
		const novoCampo: CampoDoModeloDeEtiqueta = {
			id: uuid(),
			campo: disponivel.chave,
			x: this.clamp(xCm ?? 0, 0, (this.tamanho.larguraEtiqueta || 0) - largura),
			y: this.clamp(yCm ?? 0, 0, (this.tamanho.alturaEtiqueta || 0) - altura),
			largura,
			altura,
			tamanhoDaFonte: 8,
			negrito: false,
			alinhamento: 'ESQUERDA',
			tipoDeCodigoDeBarras: disponivel.requerTipoDeCodigoDeBarras ? 'EAN13' : null,
			incluirNomeDaTabela: false,
			incluirLimitacao: false,
		}
		this.campos.push(novoCampo)
		this.campoSelecionadoIndex = this.campos.length - 1
		this.emitirMudanca()
	}

	iniciarArrasteDaPaleta(evento: DragEvent, disponivel: CampoDeEtiquetaDisponivel) {
		if (evento.dataTransfer) {
			evento.dataTransfer.setData('text/plain', disponivel.chave)
			evento.dataTransfer.effectAllowed = 'copy'
		}
	}

	soltarNaPaleta(evento: DragEvent) {
		const chave = evento.dataTransfer?.getData('text/plain') as CampoDeEtiqueta | undefined
		const disponivel = this.camposDisponiveis.find(item => item.chave === chave)
		if (!disponivel) return

		const containerRect = (this.$refs.canvas as HTMLElement).getBoundingClientRect()
		const xCm = (evento.clientX - containerRect.left) / this.escala
		const yCm = (evento.clientY - containerRect.top) / this.escala
		this.adicionarCampo(disponivel, xCm, yCm)
	}

	removerCampoSelecionado() {
		if (this.campoSelecionadoIndex === null) return
		const campo = this.campos[this.campoSelecionadoIndex]
		campo.incluirNomeDaTabela = false
		campo.incluirLimitacao = false
		this.campos.splice(this.campoSelecionadoIndex, 1)
		this.campoSelecionadoIndex = null
		this.emitirMudanca()
	}

	selecionarCampo(indice: number) {
		this.campoSelecionadoIndex = indice
	}

	deselecionarCampo() {
		this.campoSelecionadoIndex = null
	}

	iniciarArraste(evento: MouseEvent, indice: number) {
		this.selecionarCampo(indice)
		const containerRect = (this.$refs.canvas as HTMLElement).getBoundingClientRect()
		const campo = this.campos[indice]
		this.arrastando = {
			indice,
			offsetXPx: evento.clientX - (containerRect.left + campo.x * this.escala),
			offsetYPx: evento.clientY - (containerRect.top + campo.y * this.escala),
		}
		window.addEventListener('mousemove', this.moverArraste)
		window.addEventListener('mouseup', this.finalizarArraste)
	}

	moverArraste(evento: MouseEvent) {
		if (!this.arrastando) return
		const containerRect = (this.$refs.canvas as HTMLElement).getBoundingClientRect()
		const campo = this.campos[this.arrastando.indice]
		if (!campo) return

		const xCm = (evento.clientX - containerRect.left - this.arrastando.offsetXPx) / this.escala
		const yCm = (evento.clientY - containerRect.top - this.arrastando.offsetYPx) / this.escala

		campo.x = this.arredondar(this.clamp(xCm, 0, (this.tamanho.larguraEtiqueta || 0) - campo.largura))
		campo.y = this.arredondar(this.clamp(yCm, 0, (this.tamanho.alturaEtiqueta || 0) - campo.altura))
	}

	finalizarArraste() {
		window.removeEventListener('mousemove', this.moverArraste)
		window.removeEventListener('mouseup', this.finalizarArraste)
		this.arrastando = null
		this.emitirMudanca()
	}

	iniciarRedimensionamento(evento: MouseEvent, indice: number) {
		const campo = this.campos[indice]
		this.redimensionando = {
			indice,
			xInicialPx: evento.clientX,
			yInicialPx: evento.clientY,
			larguraInicial: campo.largura,
			alturaInicial: campo.altura,
		}
		window.addEventListener('mousemove', this.moverRedimensionamento)
		window.addEventListener('mouseup', this.finalizarRedimensionamento)
	}

	moverRedimensionamento(evento: MouseEvent) {
		if (!this.redimensionando) return
		const campo = this.campos[this.redimensionando.indice]
		if (!campo) return

		const deltaLarguraCm = (evento.clientX - this.redimensionando.xInicialPx) / this.escala
		const deltaAlturaCm = (evento.clientY - this.redimensionando.yInicialPx) / this.escala

		campo.largura = this.arredondar(this.clamp(
			this.redimensionando.larguraInicial + deltaLarguraCm, 0.5, (this.tamanho.larguraEtiqueta || 0) - campo.x,
		))
		campo.altura = this.arredondar(this.clamp(
			this.redimensionando.alturaInicial + deltaAlturaCm, 0.3, (this.tamanho.alturaEtiqueta || 0) - campo.y,
		))
	}

	finalizarRedimensionamento() {
		window.removeEventListener('mousemove', this.moverRedimensionamento)
		window.removeEventListener('mouseup', this.finalizarRedimensionamento)
		this.redimensionando = null
		this.emitirMudanca()
	}

	clamp(valor: number, minimo: number, maximo: number): number {
		if (maximo < minimo) return minimo
		return Math.min(Math.max(valor, minimo), maximo)
	}

	arredondar(valor: number): number {
		return Math.round(valor * 10) / 10
	}

	emitirMudanca() {
		this.$emit('input', this.campos)
	}

	@Watch('campoSelecionado', { deep: true })
	onAlterarPropriedadesDoCampo() {
		this.emitirMudanca()
	}

	beforeDestroy() {
		window.removeEventListener('mousemove', this.moverArraste)
		window.removeEventListener('mouseup', this.finalizarArraste)
		window.removeEventListener('mousemove', this.moverRedimensionamento)
		window.removeEventListener('mouseup', this.finalizarRedimensionamento)
		window.removeEventListener('keydown', this.aoPressionarDelete)
	}

	mounted() {
		window.addEventListener('keydown', this.aoPressionarDelete)
	}

	aoPressionarDelete(evento: KeyboardEvent) {
		if (evento.key !== 'Delete') return
		if (this.campoSelecionadoIndex === null) return
		const alvo = evento.target as HTMLElement | null
		if (alvo && (alvo.tagName === 'INPUT' || alvo.tagName === 'TEXTAREA' || alvo.isContentEditable)) return
		this.removerCampoSelecionado()
	}

}
